import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, UserPlus } from "lucide-react";
import { SiteHeader, PageFooter } from "@/components/brand/Brand";
import { ConceptDiagram, FlowStrip, ModuleCard, MODULE_ICONS as I } from "@/components/home/HomeSections";
import { KpiNumber } from "@/components/common/KpiNumber";
import { api } from "@/lib/api";
import { useAuth, HOME_BY_ROLE } from "@/lib/auth";
import { fmt } from "@/lib/demo/utils";
import { useI18n } from "@/lib/i18n/i18n";

const STEPS = [
  "Regístrate y sube tu CV en PDF.",
  "Recibe tu credencial con un QR personal.",
  "En el stand, escanea el QR de la empresa o muestra el tuyo.",
  "Después del Foro, envía tu CV online a las empresas que elijas.",
];

const HeroCtas = () => {
  const { user, participant } = useAuth();
  const { t } = useI18n();
  const primary = participant ? ["/credencial", "Mi credencial"] : user ? [HOME_BY_ROLE[user.role] || "/", "Mi panel"] : ["/registro", "Regístrate"];
  return (
    <div className="fade-up-3 mt-9 flex flex-wrap gap-3">
      <Link to={primary[0]} className="inline-flex items-center gap-2 rounded-full bg-upv px-6 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-upv-dark" data-testid="home-primary-cta">
        <UserPlus className="h-4 w-4" /> {t(primary[1])}
      </Link>
      <Link to="/empresas" className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3.5 text-sm font-semibold text-slate-800 transition-colors hover:border-slate-900" data-testid="home-companies-cta">
        {t("Ver empresas")} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
};

export default function Home() {
  const { participant } = useAuth();
  const { t } = useI18n();
  const p = participant || { firstName: "", lastName: "", token: "UPV-P-••••••••" };
  const [kpis, setKpis] = useState({ registrados: 0, empresas: 0, cv: 0 });
  useEffect(() => { api.get("/stats/public").then((r) => setKpis(r.data)).catch(() => {}); }, []);

  const audiences = [
    { to: participant ? "/credencial" : "/registro", icon: I.UserPlus, title: t("Estudiantes y titulados"), desc: t("Regístrate una vez, recibe tu QR y comparte tu CV con las empresas en segundos."), live: t("{n} registrados", { n: fmt(kpis.registrados) }) },
    { to: "/empresas/inscripcion", icon: I.Building2, title: t("Empresas participantes"), desc: t("Inscribe tu empresa, publica tus ofertas y recibe candidatos con su CV desde el stand."), live: t("{n} empresas", { n: fmt(kpis.empresas) }), featured: true },
    { to: "/login", icon: I.BarChart3, title: t("Equipo UPV"), desc: t("Control de accesos, gestión de empresas y analítica del Foro en tiempo real."), live: t("Acceso del equipo") },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <SiteHeader />
      <section className="mx-auto grid max-w-7xl gap-12 px-5 pb-12 pt-14 lg:grid-cols-12 lg:px-10 lg:pt-20">
        <div className="lg:col-span-6 lg:pt-6">
          <p className="fade-up text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Foro de Empleo UPV 2027 · 12–13 mayo")}</p>
          <h1 className="fade-up-1 mt-5 font-display text-4xl font-bold leading-[0.95] tracking-tight text-slate-950 sm:text-5xl lg:text-6xl" data-testid="home-title">UPV Career Fair Platform</h1>
          <p className="fade-up-2 mt-6 font-display text-2xl font-semibold text-upv sm:text-3xl" data-testid="home-subtitle">{t("Una identidad. Todo el evento.")}</p>
          <p className="fade-up-2 mt-4 max-w-lg text-base leading-relaxed text-slate-600">{t("Acceso, interacción con empresas y currículums conectados mediante una única credencial digital.")}</p>
          <HeroCtas />
          <div className="fade-up-3 mt-12 grid max-w-lg grid-cols-3 gap-6 border-t border-slate-200 pt-6">
            {[["Registrados", kpis.registrados, "home-kpi-registrados"], ["Empresas", kpis.empresas, "home-kpi-empresas"], ["CV compartidos", kpis.cv, "home-kpi-cv"]].map(([l, v, tid]) => (
              <div key={l}>
                <KpiNumber value={v} testId={tid} className="font-display text-2xl font-bold text-slate-950 sm:text-3xl" />
                <p className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-slate-500">{t(l)}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="fade-up-2 lg:col-span-6"><ConceptDiagram participant={p} /></div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="flex flex-col gap-4 border-y border-slate-200 py-6 lg:flex-row lg:items-center lg:justify-between">
          <p className="font-display text-lg font-bold tracking-tight">{t("UN QR. TODA LA EXPERIENCIA.")}</p>
          <FlowStrip />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 py-16 lg:px-10">
        <p className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400">{t("Para cada perfil")}</p>
        <h2 className="mt-2 font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("Una plataforma, tres experiencias.")}</h2>
        <div className="mt-10 grid gap-5 md:grid-cols-3">
          {audiences.map((m, i) => <ModuleCard key={m.title} n={i + 1} {...m} testId={`home-audience-${i + 1}`} />)}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-10">
        <div className="rounded-[32px] bg-slate-50 p-8 sm:p-12" data-testid="home-how-it-works">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-upv">{t("Cómo funciona")}</p>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight">{t("Del registro a la entrevista.")}</h2>
          <ol className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map((s, i) => (
              <li key={s} className="flex h-full items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid={`home-step-${i + 1}`}>
                <span className="font-mono text-xs font-bold text-upv">{String(i + 1).padStart(2, "0")}</span>
                <span className="text-sm font-semibold text-slate-800">{t(s)}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>
      <PageFooter />
    </div>
  );
}
